import {
  AdminTenantDetails,
  MonitoringTenantSummary,
  MonitoringUsersGroup,
} from '@hakimo-ui/hakimo/types';
import { Checkbox, Tooltip } from '@hakimo-ui/shared/ui-base';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import {
  ENABLE_MONITORING_CHECKLIST,
  GRANT_TENANT_ACCESS_TOOLTIP_TEXT,
} from '../util';

interface Props {
  tenant: AdminTenantDetails;
  isUpdating: boolean;
  allMonitoringTenants: MonitoringTenantSummary[];
  allHighPriorityOperatorEmails: string[];
  groups: MonitoringUsersGroup[] | undefined;
  selectedMonitoringTenants: Record<string, boolean>;
  selectedOperators: Record<string, boolean>;
  checklist: Record<string, boolean>;
  onChangeMonitoringTenant: (id: string, checked: boolean) => void;
  onChangeOperator: (email: string, checked: boolean) => void;
  onChangeChecklist: (id: string, checked: boolean) => void;
}

export function MonitoringTenantsForm({
  tenant,
  isUpdating,
  allMonitoringTenants,
  allHighPriorityOperatorEmails,
  groups,
  selectedMonitoringTenants,
  selectedOperators,
  checklist,
  onChangeMonitoringTenant,
  onChangeOperator,
  onChangeChecklist,
}: Props) {
  const accessCountByTenant = useMemo(() => {
    const map: Record<string, { withAccess: number; total: number }> = {};
    groups?.forEach((grp) => {
      map[grp.tenantId] = {
        withAccess: grp.withAccessCount,
        total: grp.totalUsers,
      };
    });
    return map;
  }, [groups]);

  return (
    <div className="max-w-xl flex-1 space-y-4 overflow-y-auto p-6">
      <p className="text-onlight-text-1 dark:text-ondark-text-1">
        Select the monitoring SOCs whose users should be granted access to{' '}
        <strong>{tenant.name}</strong> alarms.
      </p>
      <div className="space-y-2">
        {allMonitoringTenants.map((t) => {
          const count = accessCountByTenant[t.id];
          return (
            <div key={t.id} className="flex items-center gap-2">
              <Checkbox
                label={t.name}
                checked={!!selectedMonitoringTenants[t.id]}
                disabled={isUpdating}
                onChange={(checked) => onChangeMonitoringTenant(t.id, checked)}
              />
              {count && count.total > 0 && (
                <span className="text-onlight-text-2 dark:text-ondark-text-2 text-xs">
                  {count.withAccess}/{count.total} have access
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-onlight-text-1 dark:text-ondark-text-1 flex items-center gap-1 text-sm">
        <span className="font-semibold">
          Is this a high-priority tenant? Select operators to grant access.
        </span>
        <Tooltip
          text={GRANT_TENANT_ACCESS_TOOLTIP_TEXT}
          position="bottom-left"
          size="large"
        >
          <InformationCircleIcon className="text-onlight-text-1 dark:text-ondark-text-1 h-4 w-4" />
        </Tooltip>
      </div>
      <div className="space-y-2">
        {allHighPriorityOperatorEmails.map((email) => (
          <Checkbox
            key={email}
            label={email}
            checked={!!selectedOperators[email]}
            disabled={isUpdating}
            onChange={(checked) => onChangeOperator(email, checked)}
          />
        ))}
      </div>

      <div className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-semibold">
        Pre-enablement checklist
      </div>
      <div className="space-y-2">
        {ENABLE_MONITORING_CHECKLIST.map((item) => (
          <Checkbox
            key={item.id}
            label={item.label}
            checked={!!checklist[item.id]}
            disabled={isUpdating}
            onChange={(checked) => onChangeChecklist(item.id, checked)}
          />
        ))}
      </div>

      <p className="text-onlight-text-2 dark:text-ondark-text-2 text-sm">
        <strong>Note:</strong> All pending alarms in the queue for this tenant
        will be cleared when monitoring is enabled.
      </p>
    </div>
  );
}

export default MonitoringTenantsForm;
